import React, { useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { PRICING_COLUMN_TYPES } from '../../../../constants/FieldPricingTableColumnTypes';
import ThemeIcons from '../../../../theme/Icons';
import ThemeContext from '../../../../theme/ThemeContext';
import DropdownMenu from './PricingDropdownMenu';

const HeaderCellContainer = styled.th`
  font-size: 0.875rem;
  font-weight: 600;
  border-bottom: 1px solid ${(props) => props.theme.secondaryColor || '#e2e8f0'};
  text-align: left;
  position: relative;

  .pricing-table-dropdown-button {
    display: ${(props) => (props.canManageColumns ? 'flex !important' : 'none')};
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: right;
  justify-content: ${(props) => props.justifyContent || 'flex-end'};
`;

const PricingTableFieldHeaderCell = ({
  column,
  columnIndex,
  totalColumns,
  canManageColumns = true,
  onMoveColumn,
  onInsertColumn,
  onChangeColumnFormat,
  onDeleteColumn,
  onToggleColumnVisibility,
  isLastColumn
}) => {
  const theme = useContext(ThemeContext);

  const isDescriptionColumn = column.type === PRICING_COLUMN_TYPES.DESCRIPTION;
  const isDiscountColumnCellType = column.type === PRICING_COLUMN_TYPES.DISCOUNT;
  const isTaxColumnCellType = column.type === PRICING_COLUMN_TYPES.TAX;
  const isFeeColumnCellType = column.type === PRICING_COLUMN_TYPES.FEE;

  const isChangableColumnType = isDiscountColumnCellType || isTaxColumnCellType || isFeeColumnCellType;

  // Determine dropdown alignment based on column index
  const leftAligned = columnIndex <= 1; // First or second column
  const rightAligned = columnIndex >= totalColumns - 2; // Last or second to last column

  const menuItems = [];

  if (columnIndex > 0) {
    menuItems.push({
      _id: 'move-left',
      label: 'Move column left',
      onClick: () => onMoveColumn(columnIndex, 'left')
    });
  }

  if (columnIndex < totalColumns - 1) {
    menuItems.push({
      _id: 'move-right',
      label: 'Move column right',
      onClick: () => onMoveColumn(columnIndex, 'right')
    });
  }

  const insertColumnSubmenu = Object.entries(PRICING_COLUMN_TYPES).map(([key, value]) => ({
    _id: `insert-${key.toLowerCase()}`,
    label: key.split('_').map((word) => word.charAt(0) + word.slice(1).toLowerCase()).join(' '),
    onClick: () => onInsertColumn(columnIndex, value)
  }));

  menuItems.push({
    _id: 'insert-column',
    label: 'Insert column right',
    submenu: insertColumnSubmenu
  });

  // Format menu options
  if (isChangableColumnType) {
    menuItems.push({
      _id: 'column-format',
      label: 'Column number format',
      submenu: [
        {
          _id: 'format-amount',
          label: 'Currency ($)',
          onClick: () => onChangeColumnFormat(columnIndex, 'amount')
        },
        {
          _id: 'format-percent',
          label: 'Percent (%)',
          onClick: () => onChangeColumnFormat(columnIndex, 'percent')
        }
      ]
    });
  }

  menuItems.push({
    _id: 'toggle-visibility',
    label: column.hidden ? 'Show column' : 'Hide column',
    onClick: () => onToggleColumnVisibility(columnIndex)
  });

  menuItems.push({
    _id: 'delete-column',
    label: 'Delete column',
    isDelete: true,
    onClick: () => onDeleteColumn(columnIndex)
  });

  return (
    <>
      <HeaderCellContainer
        width={column.width}
        canManageColumns={canManageColumns}
        theme={theme}
        style={{ padding: '0.85rem 0.5rem' }}
      >
        {canManageColumns && (
          <DropdownMenu
            triggerIcon={ThemeIcons.misc.chevronDown}
            isDescriptionColumn={isDescriptionColumn}
            label={column.label}
            leftAligned={leftAligned}
            rightAligned={rightAligned}
            buttonStyle={{
              background: 'rgba(0, 0, 0, 0.05)',
              opacity: '0',
              marginRight: '0.5rem',
              marginTop: '-0.25rem',
              hoverSelector: 'HeaderCellContainer'
            }}
            menuItems={menuItems}
          />
        )}
        <HeaderContent justifyContent={isDescriptionColumn ? 'flex-start' : 'flex-end'}>
          {column.label}
        </HeaderContent>
      </HeaderCellContainer>

      {/* Done to neatly show the action column on the end for row controls */}
      {!isLastColumn || !canManageColumns ? null : (
        <HeaderCellContainer
          width="4%"
          canManageColumns={canManageColumns}
          theme={theme}
          style={{ padding: '0.85rem 0.5rem' }}
        />
      )}
    </>
  );
};

export default PricingTableFieldHeaderCell;

PricingTableFieldHeaderCell.propTypes = {
  column: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    format: PropTypes.string
  }).isRequired,
  columnIndex: PropTypes.number.isRequired,
  totalColumns: PropTypes.number.isRequired,
  canManageColumns: PropTypes.bool,
  onMoveColumn: PropTypes.func,
  onInsertColumn: PropTypes.func,
  onChangeColumnFormat: PropTypes.func,
  onDeleteColumn: PropTypes.func,
  onToggleColumnVisibility: PropTypes.func,
  isLastColumn: PropTypes.bool
};

